import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SETUP_SAVE_MUTATION_KEY } from '@/core/components/reputation/setup/setup-save-status';
import type { ChannelLocationStatusDto } from '@up2stay/up2promo-api-client';
import { IntegrationsApi } from '@up2stay/up2promo-api-client';
import {
  Badge,
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
  cn,
  ghostAccentIconTileClass,
  healthOkFgClass,
  healthOkSurfaceClass,
} from '@up2stay/up2promo-ui';
import { CheckCircle2, Plug } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { ConnectReviewProviderDialog } from '@/core/components/integrations/connect-review-provider-dialog';
import {
  SettingsGroup,
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { useLocations } from '@/core/hooks/use-locations';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { useChannelLocations } from '@/core/hooks/use-reputation';
import {
  channelForLocation,
  getLocationConnectionStatus,
  GOOGLE_REVIEW_PROVIDER,
  type LocationConnectionStatus,
  type ReviewProviderKey,
} from '@/core/lib/review-channel-setup';
import { formatRelativeTime } from '@/core/components/leads/lead-detail/format-relative-time';
import { useLocale } from '@/core/hooks/use-locale';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { locationsQueryKeys } from '@/core/query-keys/locations-query-keys';
import { integrationsQueryKeys } from '@/core/query-keys/integrations-query-keys';
import { reputationQueryKeys } from '@/core/query-keys/reputation-query-keys';

const CONNECTED_PROVIDERS: ReviewProviderKey[] = [GOOGLE_REVIEW_PROVIDER];

export function ReputationSetupConnectionsSection() {
  const dict = useDictionary();
  const d = dict.reputation?.setup?.connections;
  const locale = useLocale();
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const { navigateShell } = useNavigateShell();
  const queryClient = useQueryClient();

  const [providerFilter, setProviderFilter] = useState<ReviewProviderKey>(
    GOOGLE_REVIEW_PROVIDER,
  );
  const [showAll, setShowAll] = useState(false);
  const [connectLocationId, setConnectLocationId] = useState<string | null>(
    null,
  );

  const { locations, isLoading: locationsLoading } = useLocations(workspaceId, {
    limit: 100,
  });

  const { data: channelLocations = [], isLoading: channelsLoading } =
    useChannelLocations(workspaceId);

  const { data: integrationsData } = useQuery({
    queryKey: integrationsQueryKeys.all(workspaceId),
    queryFn: async () => {
      if (!workspaceId) return null;
      const response = await IntegrationsApi.list({
        path: { workspaceId },
      });
      return response.data ?? null;
    },
    enabled: !!workspaceId,
    staleTime: 60_000,
  });

  const connectedProviders = useMemo(() => {
    const connected = new Set<ReviewProviderKey>();
    for (const integration of integrationsData?.integrations ?? []) {
      if (
        integration.connected &&
        integration.provider === GOOGLE_REVIEW_PROVIDER
      ) {
        connected.add(GOOGLE_REVIEW_PROVIDER);
      }
    }
    return CONNECTED_PROVIDERS.filter((p) => connected.has(p));
  }, [integrationsData?.integrations]);

  const googleConnected = connectedProviders.includes(GOOGLE_REVIEW_PROVIDER);

  const activeProvider = connectedProviders.includes(providerFilter)
    ? providerFilter
    : (connectedProviders[0] ?? GOOGLE_REVIEW_PROVIDER);

  const needsActionLocations = useMemo(
    () =>
      locations.filter(
        (loc) =>
          getLocationConnectionStatus(loc, channelLocations, activeProvider) ===
          'needs_action',
      ),
    [locations, channelLocations, activeProvider],
  );

  const allSetupComplete =
    locations.length > 0 && needsActionLocations.length === 0;

  const filteredLocations = useMemo(() => {
    if (showAll || allSetupComplete) return locations;
    return needsActionLocations;
  }, [locations, showAll, allSetupComplete, needsActionLocations]);

  const unlinkMutation = useMutation({
    mutationKey: SETUP_SAVE_MUTATION_KEY,
    mutationFn: async (locationId: string) => {
      if (!workspaceId) return;
      await IntegrationsApi.unlink({
        path: { workspaceId, locationId },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationsQueryKeys.root });
      queryClient.invalidateQueries({ queryKey: reputationQueryKeys.root });
      toast.success(d?.unlinkSuccess);
    },
    onError: () => {
      toast.error(d?.unlinkError);
    },
  });

  const clearSkipMutation = useMutation({
    mutationKey: SETUP_SAVE_MUTATION_KEY,
    mutationFn: async (locationId: string) => {
      if (!workspaceId) return;
      await IntegrationsApi.clearSkip({
        path: { workspaceId, locationId },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationsQueryKeys.root });
      queryClient.invalidateQueries({ queryKey: reputationQueryKeys.root });
      queryClient.invalidateQueries({ queryKey: locationsQueryKeys.root });
    },
    onError: () => {
      toast.error(d?.clearSkipError);
    },
  });

  const skipMutation = useMutation({
    mutationKey: SETUP_SAVE_MUTATION_KEY,
    mutationFn: async (locationId: string) => {
      if (!workspaceId) return;
      await IntegrationsApi.skip({
        path: { workspaceId, locationId },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationsQueryKeys.root });
      queryClient.invalidateQueries({ queryKey: reputationQueryKeys.root });
      queryClient.invalidateQueries({ queryKey: locationsQueryKeys.root });
      toast.success(dict.reputation?.setup?.connectDialog?.skipSuccess);
    },
    onError: () => {
      toast.error(dict.reputation?.setup?.connectDialog?.skipError);
    },
  });

  const loading = locationsLoading || channelsLoading;
  const connectLocation = locations.find((l) => l.id === connectLocationId);

  const statusLabel = (status: LocationConnectionStatus) => {
    switch (status) {
      case 'linked':
        return d?.statusLinked;
      case 'listing':
        return d?.statusListing ?? d?.statusLinked;
      case 'opt_out':
        return d?.statusOptOut;
      default:
        return d?.statusNeedsAction;
    }
  };

  const statusVariant = (status: LocationConnectionStatus) => {
    switch (status) {
      case 'linked':
        return 'success' as const;
      case 'listing':
        return 'outline' as const;
      case 'opt_out':
        return 'secondary' as const;
      default:
        return 'warning' as const;
    }
  };

  const syncLabel = (channel?: ChannelLocationStatusDto) => {
    const status = channel?.syncStatus;
    if (!status || status === 'not_linked') return d?.syncNotLinked;
    const key = status as 'pending' | 'syncing' | 'synced' | 'failed';
    return d?.syncStatus?.[key] ?? status;
  };

  const syncDetail = (channel?: ChannelLocationStatusDto) => {
    const label = syncLabel(channel);
    if (channel?.syncStatus === 'synced' && channel.lastSyncedAt) {
      return `${label} · ${d?.lastSynced?.replace(
        '{time}',
        formatRelativeTime(channel.lastSyncedAt, locale),
      )}`;
    }
    return label;
  };

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {!googleConnected ? (
        <SettingsGroup>
          <SettingsGroupRow>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <div
                  className={cn(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--r-control)]',
                    ghostAccentIconTileClass,
                  )}
                  aria-hidden
                >
                  <Plug className="h-4 w-4" />
                </div>
                <p className="truncate text-[13.5px] font-semibold leading-tight text-foreground">
                  {d?.connectGoogleTitle}
                </p>
              </div>
              <Button
                size="sm"
                className="shrink-0"
                data-guidance-target="reputation-setup"
                onClick={() =>
                  navigateShell(shellPaths.moreWorkspaceIntegrations)
                }
              >
                {d?.connectGoogleCta}
              </Button>
            </div>
          </SettingsGroupRow>
        </SettingsGroup>
      ) : (
        <div className="flex flex-col gap-2">
          {connectedProviders.length > 0 && !allSetupComplete ? (
            <div className="flex items-center gap-2 self-end">
              <Switch
                id="show-all-connections"
                checked={showAll}
                onCheckedChange={setShowAll}
              />
              <Label
                htmlFor="show-all-connections"
                className="text-[12px] text-muted-foreground"
              >
                {d?.showAll}
              </Label>
            </div>
          ) : null}
          <SettingsGroup>
            <SettingsGroupRows>
              {connectedProviders.length > 1 ? (
                <SettingsGroupRow>
                  <div className="flex flex-col gap-1.5 max-w-xs">
                    <Label className="text-muted-foreground text-sm">
                      {d?.providerFilterLabel}
                    </Label>
                    <Select
                      value={activeProvider}
                      onValueChange={(v) =>
                        setProviderFilter(v as ReviewProviderKey)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {connectedProviders.map((provider) => (
                          <SelectItem key={provider} value={provider}>
                            {provider === GOOGLE_REVIEW_PROVIDER
                              ? d?.providerGoogle
                              : provider}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </SettingsGroupRow>
              ) : null}

              {loading ? (
                <SettingsGroupRow separated={connectedProviders.length > 1}>
                  <Skeleton className="h-16 w-full" />
                </SettingsGroupRow>
              ) : filteredLocations.length === 0 ? (
                <SettingsGroupRow separated={connectedProviders.length > 1}>
                  <p className="text-[13.5px] text-muted-foreground">
                    {d?.noLocations}
                  </p>
                </SettingsGroupRow>
              ) : (
                <>
                  {allSetupComplete ? (
                    <SettingsGroupRow separated={connectedProviders.length > 1}>
                      <div
                        className={cn(
                          'flex gap-3 rounded-[var(--r-settings-group,12px)] border p-3',
                          healthOkSurfaceClass,
                        )}
                      >
                        <CheckCircle2
                          className={cn('h-5 w-5 shrink-0', healthOkFgClass)}
                          aria-hidden
                        />
                        <p className="text-[13.5px] leading-snug text-muted-foreground">
                          {d?.allComplete}
                        </p>
                      </div>
                    </SettingsGroupRow>
                  ) : null}
                  {filteredLocations.map((loc, index) => {
                    const channel = channelForLocation(
                      channelLocations,
                      loc.id,
                      activeProvider,
                    );
                    const status = getLocationConnectionStatus(
                      loc,
                      channelLocations,
                      activeProvider,
                    );
                    const pending =
                      unlinkMutation.isPending ||
                      clearSkipMutation.isPending ||
                      skipMutation.isPending;
                    return (
                      <SettingsGroupRow
                        key={loc.id}
                        separated={
                          index > 0 ||
                          allSetupComplete ||
                          connectedProviders.length > 1
                        }
                      >
                        <div className="flex flex-wrap items-center gap-x-3.5 gap-y-3">
                          <span
                            aria-hidden
                            className="flex size-9 shrink-0 items-center justify-center rounded-[10px] border bg-[color:var(--surface-2)] text-sm font-bold text-[#4285f4]"
                          >
                            G
                          </span>
                          <div className="min-w-0 flex-1 basis-40">
                            <p className="truncate text-sm font-semibold">
                              {loc.name}
                            </p>
                            <p className="text-muted-foreground mt-px truncate text-xs">
                              {d?.providerGoogle} ·{' '}
                              {status === 'linked'
                                ? syncDetail(channel)
                                : d?.syncNotLinked}
                            </p>
                          </div>
                          <Badge variant={statusVariant(status)}>
                            {statusLabel(status)}
                          </Badge>
                          <div className="flex flex-wrap items-center gap-2">
                            {status === 'linked' ? (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={pending}
                                onClick={() => unlinkMutation.mutate(loc.id)}
                              >
                                {d?.unlink}
                              </Button>
                            ) : status === 'opt_out' ? (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={pending}
                                onClick={() => clearSkipMutation.mutate(loc.id)}
                              >
                                {d?.connect}
                              </Button>
                            ) : (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={pending}
                                  onClick={() => skipMutation.mutate(loc.id)}
                                >
                                  {d?.notUsingGoogle}
                                </Button>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => setConnectLocationId(loc.id)}
                                >
                                  {status === 'listing'
                                    ? (d?.enableSync ?? d?.link)
                                    : d?.link}
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </SettingsGroupRow>
                    );
                  })}
                </>
              )}
            </SettingsGroupRows>
          </SettingsGroup>
        </div>
      )}

      {connectLocation && workspaceId && (
        <ConnectReviewProviderDialog
          open={Boolean(connectLocationId)}
          onOpenChange={(open) => {
            if (!open) setConnectLocationId(null);
          }}
          workspaceId={workspaceId}
          location={connectLocation}
          provider={activeProvider}
        />
      )}
    </div>
  );
}
